import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../auth';
import { useLanguage } from '../i18n';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Package, ShoppingCart, TrendingUp, AlertCircle, Calendar, Users, Building2, Share2, ArrowRight } from 'lucide-react';

export default function AdminDashboard() {
  const { t } = useLanguage();
  const [stats, setStats] = useState({ new_orders: 0, total_outstanding: 0 });
  const [orders, setOrders] = useState([]);
  const [mondayProd, setMondayProd] = useState([]);
  const [thursdayProd, setThursdayProd] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, ordersRes, mondayRes, thursdayRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/orders`),
        axios.get(`${API}/production/monday`),
        axios.get(`${API}/production/thursday`),
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data.slice(0, 5));
      setMondayProd(mondayRes.data);
      setThursdayProd(thursdayRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  const formatCurrency = (val) => `₩${Number(val).toLocaleString('ko-KR')}`;

  const statusClasses = {
    new: 'status-new',
    in_production: 'status-in-production',
    packed: 'status-packed',
    shipped: 'status-shipped',
    delivered: 'status-delivered',
    cancelled: 'status-cancelled',
  };

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="eyebrow mb-2">{t('overallStats')}</p>
          <h1 className="h-display text-4xl md:text-5xl">{t('dashboard')}</h1>
        </div>
        <Button
          onClick={() => navigate('/admin/invite')}
          className="cff-button-gold h-11 px-5"
          data-testid="dashboard-invite-cta"
        >
          <Share2 className="h-4 w-4 mr-2" /> Пригласить партнёра
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-5 cff-hover-lift">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('newOrders')}</p>
              <p className="text-3xl font-bold text-gray-900 mt-2 cff-text-numeric">{stats.new_orders}</p>
            </div>
            <div className="bg-blue-50 p-2 rounded-md">
              <ShoppingCart className="h-5 w-5 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card className="p-5 cff-hover-lift">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('outstandingBalance')}</p>
              <p className="text-2xl font-bold text-gray-900 mt-2 cff-text-numeric">{formatCurrency(stats.total_outstanding)}</p>
            </div>
            <div className="bg-red-50 p-2 rounded-md">
              <AlertCircle className="h-5 w-5 text-red-600" />
            </div>
          </div>
        </Card>

        <Card className="p-5 cff-hover-lift">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('monday')}</p>
              <p className="text-3xl font-bold text-gray-900 mt-2 cff-text-numeric">{mondayProd.length}</p>
              <p className="text-xs text-gray-500 mt-0.5">{t('orders')}</p>
            </div>
            <div className="bg-amber-50 p-2 rounded-md">
              <Calendar className="h-5 w-5 text-amber-600" />
            </div>
          </div>
        </Card>

        <Card className="p-5 cff-hover-lift">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('thursday')}</p>
              <p className="text-3xl font-bold text-gray-900 mt-2 cff-text-numeric">{thursdayProd.length}</p>
              <p className="text-xs text-gray-500 mt-0.5">{t('orders')}</p>
            </div>
            <div className="bg-emerald-50 p-2 rounded-md">
              <Calendar className="h-5 w-5 text-emerald-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Recent Orders */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">{t('orders')}</h2>
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/orders')} data-testid="view-all-orders">
            View All
          </Button>
        </div>
        {orders.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No orders yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                  <th className="pb-3 font-medium">{t('orderNumber')}</th>
                  <th className="pb-3 font-medium">{t('company')}</th>
                  <th className="pb-3 font-medium">{t('plannedShipmentDate')}</th>
                  <th className="pb-3 font-medium text-right">{t('totalAmount')}</th>
                  <th className="pb-3 font-medium">{t('status')}</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(order => (
                  <tr key={order.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`order-row-${order.order_number}`}>
                    <td className="py-3 font-mono text-xs">{order.order_number}</td>
                    <td className="py-3">{order.company?.company_name}</td>
                    <td className="py-3 text-gray-600 cff-text-numeric">
                      {order.planned_shipment_date && new Date(order.planned_shipment_date).toLocaleDateString()}
                    </td>
                    <td className="py-3 text-right cff-text-numeric font-medium">{formatCurrency(order.total_amount)}</td>
                    <td className="py-3">
                      <span className={`status-badge ${statusClasses[order.status]}`}>
                        {t(order.status === 'new' ? 'newOrders' : order.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
